import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getMySellerSubscription } from "../../api/sellerSubscriptions";
import { isBespokeThreadsDomain, isOmniCommerceContext } from "../../utils";
import { canSeeSellerSubscriptionPlans } from "../../utils/sellerSubscriptionAccess";

let cachedMe = null;
let inflight = null;
const listeners = new Set();

function notifyMeListeners() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {
      /* ignore a stale subscriber */
    }
  });
}

function parseMe(res) {
  return res?.data || res || null;
}

function readOffered(data) {
  if (!data) return false;
  if (data.offered === true) return true;
  return Array.isArray(data.plans) && data.plans.length > 0;
}

function loadMe() {
  if (!inflight) {
    inflight = getMySellerSubscription()
      .then((res) => {
        cachedMe = parseMe(res);
        return cachedMe;
      })
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Full GET /seller-subscription/me. Cached per tab. Admins skip the call. */
export function useMySellerSubscription() {
  const isAdmin = useSelector(
    (s) => !!s?.currentUser?.user?.isEnterpriseAdmin
  );
  const email = useSelector((s) => s?.currentUser?.user?.email);
  const enterprise = useSelector((s) => s?.currentUser?.enterprise);
  const omniSeller = canSeeSellerSubscriptionPlans({
    isBespokeDomain: false,
    isOmniCommerce: isOmniCommerceContext(enterprise),
    email,
  });
  const [me, setMe] = useState(cachedMe);

  useEffect(() => {
    if (isAdmin || (!isBespokeThreadsDomain && !omniSeller)) {
      setMe(null);
      return undefined;
    }
    let cancelled = false;
    const apply = (value) => {
      if (!cancelled) setMe(value);
    };
    if (cachedMe) apply(cachedMe);
    loadMe().then(apply);
    const onInvalidate = () => {
      loadMe().then(apply);
    };
    listeners.add(onInvalidate);
    return () => {
      cancelled = true;
      listeners.delete(onInvalidate);
    };
  }, [isAdmin, omniSeller]);

  return {
    me,
    offered: readOffered(me),
    pendingPriceChange: me?.pendingPriceChange || null,
    downgradeNotice: me?.downgradeNotice || null,
  };
}

/** True when at least one seller plan is on. Cached per tab. */
export function useSellerSubscriptionsOffered() {
  return useMySellerSubscription().offered;
}

export function invalidateSellerSubscriptionsOffered() {
  cachedMe = null;
  inflight = null;
  notifyMeListeners();
}

export function clearCachedDowngradeNotice() {
  if (cachedMe) cachedMe = { ...cachedMe, downgradeNotice: null };
  notifyMeListeners();
}
